import { AuditedEntityDto, PagedAndSortedResultRequestDto } from '@abp/ng.core';
import { DaysOfWeek } from 'src/app/shared/shared.models';

export enum AiTestingDefinitionType {
  SimulateConversations = 0,
  TestWithHistoricalData = 1,
}

export enum EvaluationType {
  Boolean = 0,
  Integer = 1,
}

export enum AiTestingRunStatus {
  InProgress = 0,
  Success = 1,
  Error = 2,
  Cancelling = 3,
  Cancelled = 4,
  Queued = 5,
}

export interface GetAiTestingDefinitionsInput extends PagedAndSortedResultRequestDto {
  projectId: string;
  filterText?: string;
}

export interface LastEvaluationResultDto {
  dateCompleted?: Date;
  simulatedConversationCount?: number;
  successCriteriaRate?: number;
}

export interface AiTestingDefinitionSummaryDto extends AuditedEntityDto<string> {
  name: string;
  testType: AiTestingDefinitionType;
  scenarioDescription?: string;
  lastEvaluationResult?: LastEvaluationResultDto;
  scheduledRunTime?: Date;
}

export interface AiTestingDefinitionDto extends AuditedEntityDto<string> {
  name: string;
  testType: AiTestingDefinitionType;
  aiTestingDefinitionSuccessCriteria: AiTestingDefinitionSuccessCriterionDto[];
  aiTestingDefinitionSimulationScenario?: AiTestingDefinitionSimulationScenarioDto;
}

export interface AiTestingDefinitionCreateDto {
  name: string;
  testType: AiTestingDefinitionType;
  projectId: string;
  run?: boolean;
  aiTestingDefinitionSuccessCriteria: AiTestingDefinitionSuccessCriterionCreateDto[];
  aiTestingDefinitionSimulationScenario?: AiTestingDefinitionSimulationScenarioCreateDto;
}

export interface AiTestingDefinitionUpdateDto {
  name: string;
  run?: boolean;
  aiTestingDefinitionSuccessCriteria: AiTestingDefinitionSuccessCriterionUpdateDto[];
  aiTestingDefinitionSimulationScenario?: AiTestingDefinitionSimulationScenarioUpdateDto;
}

// Success Criteria
export interface AiTestingDefinitionSuccessCriterionDto {
  id: string;
  name: string;
  description: string;
  evaluationType: EvaluationType;
}

export interface AiTestingDefinitionSuccessCriterionCreateDto {
  name: string;
  description: string;
  evaluationType: EvaluationType;
}

export interface AiTestingDefinitionSuccessCriterionUpdateDto {
  id?: string;
  name: string;
  description: string;
  evaluationType: EvaluationType;
}

// Simulation Scenario
export interface AiTestingDefinitionSimulationScenarioDto {
  id: string;
  description: string;
  additionalInfo?: string;
  maxAgentMessagesCount: number;
  simulatedConversationCount: number;
  aiTestingDefinitionSimulationScenarioPersonas: AiTestingDefinitionSimulationScenarioPersonaDto[];
}

export interface AiTestingDefinitionSimulationScenarioCreateDto {
  description: string;
  additionalInfo?: string;
  maxAgentMessagesCount: number;
  simulatedConversationCount: number;
  aiTestingDefinitionSimulationScenarioPersonas: AiTestingDefinitionSimulationScenarioPersonaCreateDto[];
}

export interface AiTestingDefinitionSimulationScenarioUpdateDto {
  id?: string;
  description: string;
  additionalInfo?: string;
  maxAgentMessagesCount: number;
  simulatedConversationCount: number;
  aiTestingDefinitionSimulationScenarioPersonas: AiTestingDefinitionSimulationScenarioPersonaUpdateDto[];
}

// Personas
export interface AiTestingDefinitionSimulationScenarioPersonaDto {
  id: string;
  description: string;
}

export interface AiTestingDefinitionSimulationScenarioPersonaCreateDto {
  description: string;
}

export interface AiTestingDefinitionSimulationScenarioPersonaUpdateDto {
  id?: string;
  description: string;
}

export enum AiTestingRecurrenceFrequency {
  Daily = 0,
  Weekly = 1,
  Monthly = 2,
}

export interface AiTestingScheduledRunDto {
  isRecurring: boolean;
  runDateTime: string;
  recurrenceRangeEndDate?: string;
  nextRunDateTime?: string;
  recurrenceFrequency?: AiTestingRecurrenceFrequency;
  scheduledDaysOfWeek: DaysOfWeek[];
  scheduledDayOfMonth?: number;
}

export interface CreateUpdateAiTestingScheduledRunDto {
  isRecurring: boolean;
  runDateTime: string;
  recurrenceRangeEndDate?: string;
  recurrenceFrequency?: AiTestingRecurrenceFrequency;
  scheduledDaysOfWeek?: DaysOfWeek[];
  scheduledDayOfMonth?: number;
}

// AI Testing Runs
export type GetAiTestingRunsInput = PagedAndSortedResultRequestDto;

export interface AiTestingRunSummaryDto {
  id: string;
  dateStarted?: Date;
  duration?: number;
  simulatedConversationCount?: number;
  totalConversationCount?: number;
  successCriteriaRate?: number;
  status: AiTestingRunStatus;
}

export interface AiTestingRunDto {
  dateStarted?: Date;
  dateCompleted?: Date;
  duration?: number;
  status: AiTestingRunStatus;
  successCriteriaRate?: number;
  agentTokenConsumption: AiTestingRunTokenConsumptionDto;
  tokenConsumption: AiTestingRunTokenConsumptionDto;
  conversation: AiTestingRunConversationDetailDto;
  aiAgent: AiTestingRunAiAgentDetailDto;
  successCriteria: Record<string, number | null>;
  aiTestingRunSessions: AiTestingRunSessionDto[];
}

export interface AiTestingRunTokenConsumptionDto {
  inputTokenCount: number;
  cachedInputTokenCount: number;
  outputTokenCount: number;
}

export interface AiTestingRunConversationDetailDto {
  averageAgentMessages: number;
  averageDuration?: number;
  conversationCount: number;
}

export interface AiTestingRunAiAgentDetailDto {
  averageInputTokenConsumption?: number;
  averageOutputTokenConsumption?: number;
  averageResponseTime?: number;
  isAiAgentWidgetVisible: boolean;
}

export interface AiTestingRunSessionDto {
  conversationSessionId: string;
  agentMessages: number;
  duration?: number;
  score?: number;
  aiTestingEvaluationResults: AiTestingEvaluationResultDto[];
}

export interface AiTestingEvaluationResultDto {
  successCriterionId: string;
  successCriterionName: string;
  evaluationType?: EvaluationType;
  resultScore?: number;
  result: boolean;
  score?: number;
  rationale?: string;
}

// Export / Import
export interface AiTestingDefinitionNameDto {
  id: string;
  name: string;
}

export interface AiTestingDefinitionExportInputDto {
  idList: string[];
}

export interface AiTestingDefinitionImportInputDto {
  projectId: string;
  jsonList: string[];
}
